/**
 * Audio Sanctum — Shared Styled Components
 * ==========================================
 * Reusable UI building blocks. Import these instead of defining local styled components.
 * All values reference design-system.ts tokens.
 *
 * Usage:
 *   import { Card, SectionLabel, TextButton, FormInput } from "@/styles/shared-components";
 */

import styled, { css } from "styled-components";

import {
  colors,
  components,
  fonts,
  fontSizes,
  fontWeights,
  radius,
  shadows,
  transitions,
} from "./design-system";

/* ═══════════════════════════════════════════
   LAYOUT
   ═══════════════════════════════════════════ */

/** Vertical section with bottom margin */
export const Section = styled.div`
  margin-bottom: 32px;
`;

/** Flex row for section header (label + optional right action) */
export const SectionHeader = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: 16px;
`;

/* ═══════════════════════════════════════════
   TYPOGRAPHY
   ═══════════════════════════════════════════ */

/** Page title — large Cormorant heading ("Account", "Browse Music") */
export const PageTitle = styled.h1`
  font-family: ${fonts.heading};
  font-size: ${fontSizes["3xl"]};
  font-weight: ${fontWeights.bold};
  color: ${colors.text.heading};
  margin: 0;
`;

/** Section label — small uppercase Raleway ("USER", "PASSWORD", "BILLING ADDRESSES") */
export const SectionLabel = styled.h2`
  font-family: ${fonts.body};
  font-size: ${fontSizes.sm};
  font-weight: ${fontWeights.medium};
  text-transform: uppercase;
  letter-spacing: 2px;
  color: ${colors.text.primary};
  margin: 0;
  padding-left: 6px;
`;

/** Body text — standard readable text */
export const BodyText = styled.div`
  font-family: ${fonts.body};
  font-size: ${fontSizes.body};
  color: ${colors.text.muted};
  line-height: 1.6;
`;

/** Small muted caption text */
export const CaptionText = styled.div`
  font-family: ${fonts.body};
  font-size: ${fontSizes.sm};
  color: ${colors.text.muted};
  line-height: 1.5;
`;

/* ═══════════════════════════════════════════
   CARDS
   ═══════════════════════════════════════════ */

/** Standard card container */
export const Card = styled.div`
  background: ${colors.surface.card};
  border: 1px solid ${colors.border.light};
  border-radius: ${radius.xl};
  padding: 20px;
`;

/** Card with overflow hidden (for rows with internal borders) */
export const CardContainer = styled.div`
  background: ${colors.surface.card};
  border: 1px solid ${colors.border.light};
  border-radius: ${radius.xl};
  overflow: hidden;
`;

/** Selectable card (e.g. address cards) */
export const SelectableCard = styled.div<{ $selected?: boolean }>`
  background: ${colors.surface.card};
  border: 1px solid ${({ $selected }) => ($selected ? "rgba(156, 211, 253, 0.4)" : colors.border.light)};
  border-radius: ${radius.xl};
  padding: 20px;
  cursor: pointer;
  transition: all ${transitions.slow}, opacity 0.4s ease, transform 0.4s ease;
  display: flex;
  flex-direction: column;
  animation: cardFadeIn 0.4s ease;

  @keyframes cardFadeIn {
    from { opacity: 0.6; transform: scale(0.97); }
    to { opacity: 1; transform: scale(1); }
  }

  &:hover {
    border-color: ${({ $selected }) => ($selected ? "rgba(156, 211, 253, 0.5)" : colors.border.default)};
    background: ${colors.surface.cardHover};
  }
`;

/** Dashed add-item card (e.g. "Add Address") */
export const AddCard = styled.button`
  background: rgba(81, 58, 40, 0.25);
  border: 2px dashed ${colors.border.input};
  border-radius: ${radius.xl};
  padding: 32px 20px;
  cursor: pointer;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 10px;
  transition: all ${transitions.normal};
  min-height: 160px;

  &:hover {
    border-color: ${colors.border.default};
    background: rgba(81, 58, 40, 0.45);
  }

  svg {
    width: 28px;
    height: 28px;
    stroke: rgba(235, 194, 142, 0.5);
    transition: stroke ${transitions.normal};
  }

  &:hover svg {
    stroke: ${colors.text.link};
  }
`;

export const AddCardLabel = styled.span`
  font-family: ${fonts.body};
  font-size: ${fontSizes.body};
  font-weight: ${fontWeights.medium};
  color: rgba(235, 194, 142, 0.6);
  transition: color ${transitions.normal};

  ${AddCard}:hover & {
    color: ${colors.text.link};
  }
`;

/* ═══════════════════════════════════════════
   INFO ROWS (for displaying key-value data in cards)
   ═══════════════════════════════════════════ */

/** Horizontal row inside a CardContainer — icon + text + action */
export const InfoRow = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 16px;
  padding: 16px 20px;
  flex-wrap: wrap;

  &:not(:last-child) {
    border-bottom: 1px solid ${colors.border.veryLight};
  }

  /* Phone + tablet: stack so the action always sits at the bottom-left of the
     card instead of sometimes staying inline-right and sometimes wrapping
     (depending on how long the value is). Uniform placement across every card. */
  @media (max-width: 1149.98px) {
    flex-direction: column;
    align-items: flex-start;
    gap: 12px;
  }
`;

export const InfoLeft = styled.div`
  display: flex;
  /* Top-align the section icon with the heading so it sits in the SAME spot in
     every section, regardless of how many description lines follow (centering
     made the icon drift down as content grew). */
  align-items: flex-start;
  gap: 12px;
  min-width: 0;
`;

export const InfoIconCircle = styled.div<{ $color?: string }>`
  width: 36px;
  height: 36px;
  border-radius: 50%;
  background: ${({ $color }) => $color || "rgba(156, 211, 253, 0.12)"};
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;

  svg {
    width: 16px;
    height: 16px;
    stroke: ${({ $color }) => ($color ? colors.text.primary : colors.accent.blue)};
  }
`;

export const InfoTextWrap = styled.div`
  min-width: 0;
`;

/** Tiny uppercase label above a value (e.g. "EMAIL ADDRESS") */
export const InfoLabel = styled.div`
  font-family: ${fonts.body};
  font-size: ${fontSizes.sm};
  font-weight: ${fontWeights.medium};
  color: ${colors.text.mutedLight};
  margin-bottom: 2px;
  text-transform: uppercase;
  letter-spacing: 0.05em;
`;

/** Value text (e.g. the actual email or name) */
export const InfoValue = styled.div`
  font-family: ${fonts.body};
  font-size: ${fontSizes.body};
  font-weight: ${fontWeights.medium};
  color: ${colors.text.primary};
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
`;

/** Inline edit row that slides in below an InfoRow */
export const EditRow = styled.div`
  padding: 14px 20px;
  border-top: 1px solid ${colors.border.veryLight};
`;

/** Two-column label:value grid */
export const DataGrid = styled.div`
  display: grid;
  grid-template-columns: auto 1fr;
  gap: 6px 16px;
`;

export const DataLabel = styled.span`
  font-family: ${fonts.body};
  font-size: ${fontSizes.body};
  color: rgba(251, 244, 236, 0.4);
`;

export const DataValue = styled.span`
  font-family: ${fonts.body};
  font-size: ${fontSizes.body};
  color: ${colors.text.primary};
  font-weight: ${fontWeights.medium};
`;

/* ═══════════════════════════════════════════
   BADGES
   ═══════════════════════════════════════════ */

/** Small pill badge (e.g. "Default", "3") */
export const Badge = styled.span<{ $color?: "blue" | "green" | "gold" }>`
  font-family: ${fonts.number};
  font-size: ${fontSizes.xs};
  font-weight: ${fontWeights.semibold};
  padding: 3px 10px;
  border-radius: 12px;
  background: ${({ $color }) =>
    $color === "green"
      ? colors.accent.greenBg
      : $color === "gold"
        ? "rgba(186, 144, 92, 0.15)"
        : "rgba(156, 211, 253, 0.15)"};
  color: ${({ $color }) =>
    $color === "green"
      ? colors.accent.green
      : $color === "gold"
        ? colors.gold.accent
        : colors.accent.blue};
`;

/* ═══════════════════════════════════════════
   BUTTONS
   ═══════════════════════════════════════════ */

/** Blue text button for inline actions ("Edit", "Change", "Set as Default") */
export const TextButton = styled.button`
  background: none;
  border: none;
  color: ${colors.accent.blue};
  font-family: ${fonts.body};
  font-size: ${fontSizes.body};
  font-weight: ${fontWeights.medium};
  cursor: pointer;
  padding: 6px 12px;
  border-radius: ${radius.sm};
  transition: all ${transitions.fast};
  white-space: nowrap;

  &:hover {
    background: rgba(156, 211, 253, 0.1);
  }
`;

/** Small text link for card footers ("Edit | Remove | Set as Default") */
export const ActionLink = styled.button<{ $danger?: boolean }>`
  background: none;
  border: none;
  color: ${({ $danger }) => ($danger ? colors.accent.red : colors.accent.blue)};
  font-family: ${fonts.body};
  font-size: ${fontSizes.sm};
  font-weight: ${fontWeights.medium};
  cursor: pointer;
  padding: 4px 8px;
  border-radius: ${radius.xs};
  transition: all ${transitions.fast};

  &:hover {
    background: ${({ $danger }) => ($danger ? "rgba(248, 113, 113, 0.1)" : "rgba(156, 211, 253, 0.1)")};
  }
`;

export const ActionSep = styled.span`
  color: rgba(251, 244, 236, 0.15);
  font-size: ${fontSizes.sm};
  line-height: 1;
  display: flex;
  align-items: center;
`;

/** Calm row action — a plain blue text link on desktop (identical to the old
    ActionLink) that becomes a calm, bordered button with a blue leading icon on
    phone/tablet, so every card's action looks the same and (paired with the
    InfoRow stack) sits at the row's bottom-left. The leading icon must be the
    FIRST child; it's hidden on desktop so the desktop look is unchanged. */
export const RowAction = styled.button<{ $danger?: boolean }>`
  display: inline-flex;
  align-items: center;
  gap: 6px;
  background: none;
  border: none;
  color: ${({ $danger }) => ($danger ? colors.accent.red : colors.accent.blue)};
  font-family: ${fonts.body};
  font-size: ${fontSizes.sm};
  font-weight: ${fontWeights.medium};
  cursor: pointer;
  padding: 4px 8px;
  border-radius: ${radius.xs};
  transition: all ${transitions.fast};
  white-space: nowrap;

  svg {
    width: 15px;
    height: 15px;
    flex-shrink: 0;
  }

  /* Leading icon is a phone/tablet affordance only — keep desktop identical to
     the old plain text link. */
  & > svg:first-child {
    display: none;
  }

  &:hover {
    background: ${({ $danger }) => ($danger ? "rgba(248, 113, 113, 0.1)" : "rgba(156, 211, 253, 0.1)")};
  }

  @media (max-width: 1149.98px) {
    padding: 10px 16px;
    border-radius: 12px;
    border: 1px solid ${({ $danger }) => ($danger ? "rgba(248, 113, 113, 0.4)" : "rgba(186, 144, 92, 0.4)")};
    background: rgba(255, 255, 255, 0.05);
    color: ${({ $danger }) => ($danger ? "#f0a89e" : "#fbf4ec")};

    & > svg:first-child {
      display: inline-flex;
      color: ${({ $danger }) => ($danger ? "#f87171" : "#9cd3fd")};
    }

    &:hover {
      background: rgba(255, 255, 255, 0.07);
      border-color: ${({ $danger }) => ($danger ? "rgba(248, 113, 113, 0.55)" : "rgba(186, 144, 92, 0.55)")};
    }
  }
`;

/** Gold primary action button.
 *  `$dimmed` gives the greyed "disabled" look while staying clickable, so a
 *  click on an incomplete form still fires submit and shakes the unmet required
 *  fields (same pattern as the modals' dimmed submit button). */
export const PrimaryButton = styled.button<{ $dimmed?: boolean }>`
  padding: 10px 20px;
  background: ${components.buttonPrimary.background};
  color: ${colors.brown.darkest};
  border: 1px solid transparent;
  border-radius: ${radius.md};
  font-family: ${fonts.body};
  font-size: ${fontSizes.body};
  font-weight: ${fontWeights.semibold};
  cursor: pointer;
  transition: all ${transitions.normal};

  /* Icon + label on one line, for the send-message buttons on the contact
     forms. Harmless on buttons that carry no icon. */
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 8px;

  svg {
    flex-shrink: 0;
    /* #60a3d7 — the site-wide blue for icons on gold buttons. */
    color: #60a3d7;
    transition: color ${transitions.normal};
  }

  &:hover {
    transform: translateY(-1px);
    box-shadow: ${shadows.glow.gold};
  }

  /* Greyed out, the button turns brown and its label fades. A blue icon on that
     brown reads as still-active, so the icon fades with the label — a light
     warm tone that stays legible on the card without claiming to be clickable. */
  &:disabled svg {
    color: rgba(235, 194, 142, 0.45);
  }

  &:disabled {
    background: ${colors.surface.card};
    color: rgba(251, 244, 236, 0.3);
    border: 1px solid ${colors.border.veryLight};
    transform: none;
    box-shadow: none;
    cursor: default;
  }

  ${({ $dimmed }) =>
    $dimmed &&
    css`
      background: ${colors.surface.card};
      color: rgba(251, 244, 236, 0.3);
      border: 1px solid ${colors.border.veryLight};
      box-shadow: none;

      svg {
        color: rgba(235, 194, 142, 0.45);
      }

      &:hover {
        transform: none;
        box-shadow: none;
      }
    `}
`;

/** Outlined secondary button */
export const SecondaryButton = styled.button`
  padding: 10px 20px;
  background: transparent;
  border: 1px solid ${colors.border.default};
  border-radius: ${radius.md};
  color: ${colors.text.link};
  font-family: ${fonts.body};
  font-size: ${fontSizes.body};
  font-weight: ${fontWeights.medium};
  cursor: pointer;
  transition: all ${transitions.normal};

  &:hover {
    background: rgba(235, 194, 142, 0.08);
  }

  &:disabled {
    opacity: 0.5;
    cursor: default;
  }
`;

/** Red danger button ("Delete Account") */
export const DangerButton = styled.button`
  display: inline-flex;
  align-items: center;
  gap: 8px;
  padding: 10px 20px;
  background: transparent;
  border: 1px solid rgba(248, 113, 113, 0.3);
  border-radius: ${radius.md};
  color: #f87171;
  font-family: ${fonts.body};
  font-size: ${fontSizes.body};
  font-weight: ${fontWeights.medium};
  cursor: pointer;
  transition: all ${transitions.normal};

  &:hover {
    background: rgba(248, 113, 113, 0.1);
    border-color: rgba(248, 113, 113, 0.5);
  }
`;

/* ═══════════════════════════════════════════
   ACCOUNT BANNERS (status / alert strips)
   ═══════════════════════════════════════════ */

/** Banner shell — a text block + a side action on desktop that stacks
    vertically (text on top, full-width action below) on phone/tablet so the
    side button never squeezes the copy into a cramped column. Dynamic styling
    (background colour, the collapse animation's padding/max-height/opacity)
    stays inline per-banner; this owns only the responsive layout. */
export const BannerShell = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 16px;
  overflow: hidden;

  @media (max-width: 1149.98px) {
    flex-direction: column;
    align-items: stretch;
    gap: 14px;
  }
`;

/** Gold CTA used inside a BannerShell — full-width on phone/tablet so it reads
    as a proper button under the stacked copy. Hover is CSS (no JS handlers). */
export const BannerCTA = styled.button`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
  flex-shrink: 0;
  white-space: nowrap;
  padding: 10px 20px;
  border-radius: ${radius.md};
  background: linear-gradient(135deg, #ffd29b 0%, #ffebb5 100%);
  border: none;
  color: ${colors.brown.darkest};
  font-family: ${fonts.body};
  font-size: ${fontSizes.body};
  font-weight: ${fontWeights.semibold};
  cursor: pointer;
  transition: all ${transitions.normal};
  box-shadow: 0 0 16px rgba(186, 144, 92, 0.25);

  &:hover {
    box-shadow: 0 4px 24px rgba(186, 144, 92, 0.5);
    transform: translateY(-1px);
  }

  @media (max-width: 1149.98px) {
    width: 100%;
  }
`;

/* ═══════════════════════════════════════════
   FORMS
   ═══════════════════════════════════════════ */

/** Inline form row (input + buttons side by side) */
export const InlineFormRow = styled.div`
  display: flex;
  gap: 10px;
  align-items: flex-start;
  flex-wrap: wrap;
`;

/** Form grid (2 columns on desktop, 1 on mobile) */
export const FormGrid = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 14px;

  @media (max-width: 600px) {
    grid-template-columns: 1fr;
  }
`;

/** Form group wrapper, $full spans both columns */
export const FormGroup = styled.div<{ $full?: boolean }>`
  grid-column: ${({ $full }) => ($full ? "1 / -1" : "auto")};
`;

/** Uppercase form label */
export const FormLabel = styled.label`
  display: block;
  font-family: ${fonts.body};
  font-size: ${fontSizes.sm};
  font-weight: ${fontWeights.medium};
  color: rgba(251, 244, 236, 0.55);
  margin-bottom: 6px;
  text-transform: uppercase;
  letter-spacing: 0.04em;
`;

/** Standard text input */
export const FormInput = styled.input<{ $error?: boolean }>`
  width: 100%;
  padding: 10px 14px;
  background: rgba(75, 55, 39, 0.8);
  border: 1px solid ${({ $error }) => ($error ? "rgba(248, 113, 113, 0.5)" : colors.border.light)};
  border-radius: ${radius.md};
  color: ${colors.text.primary};
  font-family: ${fonts.body};
  font-size: ${fontSizes.body};
  outline: none;
  transition: border-color ${transitions.normal};

  &::placeholder {
    color: rgba(251, 244, 236, 0.25);
  }

  &:focus {
    border-color: rgba(156, 211, 253, 0.4);
  }
`;

/** Glassy golden text input — warm translucent glass with white text */
export const FormInputGlassy = styled.input<{ $error?: boolean }>`
  width: 100%;
  padding: 14px 16px;
  background: rgba(235, 194, 142, 0.06);
  border: 1px solid ${({ $error }) => ($error ? "rgba(248, 113, 113, 0.5)" : "rgba(235, 194, 142, 0.25)")};
  border-radius: ${radius.md};
  color: #ffffff;
  font-family: ${fonts.body};
  font-size: ${fontSizes.body};
  outline: none;
  transition: all ${transitions.normal};
  backdrop-filter: blur(12px);
  -webkit-backdrop-filter: blur(12px);

  &::placeholder {
    color: rgba(255, 255, 255, 0.6);
  }

  &:focus {
    background: rgba(235, 194, 142, 0.1);
    border-color: rgba(235, 194, 142, 0.5);
    box-shadow: 0 0 12px rgba(186, 144, 92, 0.15);
  }

  &:hover:not(:focus) {
    border-color: rgba(235, 194, 142, 0.35);
  }
`;

/** Standard select dropdown */
export const FormSelect = styled.select<{ $error?: boolean }>`
  width: 100%;
  padding: 10px 14px;
  background: rgba(75, 55, 39, 0.8);
  border: 1px solid ${({ $error }) => ($error ? "rgba(248, 113, 113, 0.5)" : colors.border.light)};
  border-radius: ${radius.md};
  color: ${colors.text.primary};
  font-family: ${fonts.body};
  font-size: ${fontSizes.body};
  outline: none;
  transition: border-color ${transitions.normal};
  cursor: pointer;
  appearance: none;
  background-image: url("data:image/svg+xml,%3Csvg width='10' height='6' viewBox='0 0 10 6' fill='none' xmlns='http://www.w3.org/2000/svg'%3E%3Cpath d='M1 1L5 5L9 1' stroke='%23ebc28e' stroke-width='1.5' stroke-linecap='round' stroke-linejoin='round'/%3E%3C/svg%3E");
  background-repeat: no-repeat;
  background-position: right 14px center;
  padding-right: 36px;

  &:focus {
    border-color: rgba(156, 211, 253, 0.4);
  }

  option {
    background: ${colors.surface.modal};
    color: ${colors.text.primary};
  }
`;

/** Form validation error message */
export const FormError = styled.div`
  font-family: ${fonts.body};
  font-size: ${fontSizes.xs};
  color: #f87171;
  margin-top: 4px;
`;

/** Button row aligned right */
export const FormActions = styled.div`
  display: flex;
  gap: 10px;
  margin-top: 20px;
  justify-content: flex-end;
`;

/** Inline form card (for add/edit forms that appear below a grid) */
export const FormCard = styled.div`
  background: ${colors.surface.card};
  border: 1px solid rgba(156, 211, 253, 0.3);
  border-radius: ${radius.xl};
  padding: 24px;
  margin-top: 16px;
`;

/** Form card title */
export const FormCardTitle = styled.h3`
  font-family: ${fonts.heading};
  font-size: ${fontSizes.lg};
  font-weight: ${fontWeights.semibold};
  color: ${colors.text.primary};
  margin: 0 0 16px;
`;
